/**
 * LodestarMark — the brand emblem.
 *
 * A guiding 4-point star inside a thin gold crescent arc, surrounded by
 * dashed orbital rings, with subtle constellation-style points marking
 * the cardinal compass nodes. Pure SVG — scales infinitely, no raster
 * asset, no third-party iconography.
 *
 * Props
 * ─────
 *   size       outer pixel size (default 96)
 *   showHalo   render a soft purple/gold radial halo behind the mark
 *   variant    "default" | "mono"  ("mono" drops the halo + simplifies for tiny use)
 */
export default function LodestarMark({ size = 96, showHalo = true, variant = "default", className = "" }) {
    const mono = variant === "mono";
    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="-50 -50 100 100"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Lodestar"
        >
            <defs>
                <radialGradient id="ls-halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%"  stopColor="#D4AF37" stopOpacity="0.35" />
                    <stop offset="55%" stopColor="#7C3AED" stopOpacity="0.10" />
                    <stop offset="100%" stopColor="#0E0820" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="ls-crescent" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%"   stopColor="#F3E5AB" />
                    <stop offset="55%"  stopColor="#D4AF37" />
                    <stop offset="100%" stopColor="#B48E2D" />
                </linearGradient>
                <linearGradient id="ls-star" x1="50%" y1="0%" x2="50%" y2="100%">
                    <stop offset="0%"   stopColor="#FFF8DC" />
                    <stop offset="100%" stopColor="#D4AF37" />
                </linearGradient>
            </defs>

            {/* soft halo */}
            {showHalo && !mono && <circle cx="0" cy="0" r="48" fill="url(#ls-halo)" />}

            {/* outermost orbit (gold dotted) */}
            <circle cx="0" cy="0" r="42" fill="none" stroke="#D4AF37" strokeOpacity="0.35"
                    strokeWidth="0.6" strokeDasharray="0.6 4" />
            {/* mid orbit (silver dotted) */}
            <circle cx="0" cy="0" r="34" fill="none" stroke="#E9DEFF" strokeOpacity="0.30"
                    strokeWidth="0.5" strokeDasharray="1 5" />
            {/* inner orbit (gold thin solid) */}
            <circle cx="0" cy="0" r="26" fill="none" stroke="#F3E5AB" strokeOpacity="0.45"
                    strokeWidth="0.45" />

            {/* gold crescent — a sliver carved out of two arcs */}
            <path
                d="M -28 0
                   A 28 28 0 1 1 28 0
                   A 22 22 0 1 0 -22 0
                   Z"
                fill="url(#ls-crescent)"
                opacity="0.92"
            />

            {/* white inner crescent counterpart (the right-side bright sliver) */}
            <path
                d="M 28 0
                   A 28 28 0 0 1 0 28
                   A 22 22 0 0 0 22 0
                   Z"
                fill="#F5F1E6"
                opacity="0.85"
            />

            {/* 4-point guiding star at center — long vertical, short horizontal */}
            <path
                d="M 0 -22 L 4 -4 L 22 0 L 4 4 L 0 22 L -4 4 L -22 0 L -4 -4 Z"
                fill="url(#ls-star)"
                stroke="#F8F2D8"
                strokeWidth="0.4"
            />

            {/* 4 cardinal compass dots on the inner orbit */}
            <circle cx="0"   cy="-26" r="1.4" fill="#F3E5AB" />
            <circle cx="26"  cy="0"   r="1.4" fill="#F3E5AB" />
            <circle cx="0"   cy="26"  r="1.4" fill="#F3E5AB" />
            <circle cx="-26" cy="0"   r="1.4" fill="#F3E5AB" />

            {/* 3 micro stars along the vertical orbit — constellation feel */}
            <circle cx="0" cy="-34" r="0.9" fill="#FFFFFF" opacity="0.8" />
            <circle cx="0" cy="-12" r="0.9" fill="#FFFFFF" opacity="0.85" />
            <circle cx="0" cy="14"  r="0.9" fill="#FFFFFF" opacity="0.85" />
        </svg>
    );
}
